import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import CompanyCredits from './CompanyCredits';
import CompanyCreditsExportModal from './CompanyCreditsExportModal';
import { formatCNPJ } from '../utils/formatters';

interface CompanyCreditsRow {
  id: number;
  name: string;
  cnpj: string | null;
  status: number;
  created_at: string;
  opportunities: number;
  highlights: number;
  confidentials: number;
  total_credits: number;
}

const CompanyCreditsList: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyCreditsRow[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [scope, setScope] = useState('all');
  const [creditsFilter, setCreditsFilter] = useState('all');
  const [showCredits, setShowCredits] = useState(false);
  const [creditsCompany, setCreditsCompany] = useState<CompanyCreditsRow | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, [pagination.page, searchTerm, scope, creditsFilter]);

  const buildFilterParams = () => new URLSearchParams({
    ...(scope !== 'all' && { status: scope === 'active' ? '1' : '0' }),
    ...(creditsFilter !== 'all' && { hasCredits: creditsFilter === 'with' ? '1' : '0' }),
    ...(searchTerm && { search: searchTerm, searchType }),
  });

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = buildFilterParams();
      params.set('page', pagination.page.toString());
      params.set('limit', pagination.limit.toString());

      const response = await api.get(`/company-credits?${params}`);
      setCompanies(response.data.companies);
      setPagination(response.data.pagination);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        toast.error('Erro ao carregar créditos das empresas');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchType === 'cnpj') {
      const numbers = value.replace(/\D/g, '');
      const formatted = numbers.length <= 14
        ? numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        : numbers.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      setSearch(formatted);
    } else {
      setSearch(value);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleManageCredits = (company: CompanyCreditsRow) => {
    setCreditsCompany(company);
    setShowCredits(true);
  };

  const handleCloseCredits = () => {
    setShowCredits(false);
    setCreditsCompany(null);
    fetchCompanies();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Créditos</h1>
        <button onClick={() => setShowExportModal(true)} style={styles.exportBtn}>
          Exportar Excel
        </button>
      </div>

      <div style={styles.scopes}>
        <button
          onClick={() => { setScope('all'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(scope === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button
          onClick={() => { setScope('active'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(scope === 'active' ? styles.scopeBtnActive : {})}}
        >
          Ativo
        </button>
        <button
          onClick={() => { setScope('inactive'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(scope === 'inactive' ? styles.scopeBtnActive : {})}}
        >
          Inativo
        </button>
        <div style={styles.divider}></div>
        <button
          onClick={() => { setCreditsFilter('all'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(creditsFilter === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button
          onClick={() => { setCreditsFilter('with'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(creditsFilter === 'with' ? styles.scopeBtnActive : {})}}
        >
          Com Créditos
        </button>
        <button
          onClick={() => { setCreditsFilter('without'); setPagination(prev => ({ ...prev, page: 1 })); }}
          style={{...styles.scopeBtn, ...(creditsFilter === 'without' ? styles.scopeBtnActive : {})}}
        >
          Sem Créditos
        </button>
      </div>

      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="name">Nome</option>
          <option value="cnpj">CNPJ</option>
          <option value="id">ID</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${searchType === 'name' ? 'nome' : searchType === 'cnpj' ? 'CNPJ' : 'ID'}...`}
          value={search}
          onChange={handleSearchChange}
          style={styles.input}
          maxLength={searchType === 'cnpj' ? 18 : undefined}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>Cnpj</th>
                  <th style={styles.thCenter}>Vagas</th>
                  <th style={styles.thCenter}>Destaques</th>
                  <th style={styles.thCenter}>Confidenciais</th>
                  <th style={styles.thCenter}>Total</th>
                  <th style={styles.th}>Ativo</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id} style={styles.tableRow}>
                    <td style={styles.td}>{company.id}</td>
                    <td style={styles.companyName}>{company.name}</td>
                    <td style={styles.td}>{formatCNPJ(company.cnpj)}</td>
                    <td style={styles.tdCredit}>{company.opportunities}</td>
                    <td style={styles.tdCredit}>{company.highlights}</td>
                    <td style={styles.tdCredit}>{company.confidentials}</td>
                    <td style={styles.tdTotal}>{company.total_credits}</td>
                    <td style={styles.td}>
                      {company.status === 1 ? (
                        <span style={styles.activeBadge}>SIM</span>
                      ) : (
                        <span style={styles.inactiveBadge}>NÃO</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleManageCredits(company)}
                        style={styles.manageBtn}
                      >
                        Gerenciar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}

      {showExportModal && (
        <CompanyCreditsExportModal
          initialFilters={{ scope, creditsFilter, search: searchTerm, searchType }}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {showCredits && creditsCompany && (
        <CompanyCredits
          companyId={creditsCompany.id}
          companyName={creditsCompany.name}
          onClose={handleCloseCredits}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    margin: 0,
  },
  exportBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  divider: {
    width: '1px',
    backgroundColor: colors.border,
    margin: '0 0.5rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  thCenter: {
    padding: '1rem',
    textAlign: 'center' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  tdCredit: {
    padding: '1rem',
    color: colors.text,
    textAlign: 'center' as const,
    fontWeight: 'bold' as const,
  },
  tdTotal: {
    padding: '1rem',
    color: colors.primary,
    textAlign: 'center' as const,
    fontWeight: 'bold' as const,
  },
  companyName: {
    maxWidth: '400px',
    padding: '1rem',
    color: colors.text,
  },
  activeBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  inactiveBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  manageBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
};

export default CompanyCreditsList;
